import React, { useState } from 'react'
import Layout from '../components/layout'
import Meta from '../components/meta'
import tw, { styled, css } from 'twin.macro'
import { navigate } from 'gatsby'
import { graphql, Link } from 'gatsby'
import PropTypes from 'prop-types'
import Img from 'gatsby-image'
import CORDLESS_HP from '../images/CORDLESS_HP.svg'
import AccordionIndex from '../components/liberty-cordless/Accordion'
import VideoPlayer from '../components/video-player'

const GreySection = styled.section`
	font-family: Inter;
	font-style: normal;
	text-align: center;
	background-color: rgb(51 65 85 / var(--tw-bg-opacity, 1));
	margin-top: -2px;
	line-height: 1.5;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 50px 20%;

	@media only screen and (max-width: 1024px) {
		padding: 30px 20%;
	}

	@media only screen and (max-width: 600px) {
		padding: 30px 10%;
	}
`

const WhiteSection = styled.section`
	font-family: Inter;
	font-style: normal;
	align-items: flex-start;

	display: flex;
	flex-direction: column;
	padding: 50px 20%;

	@media only screen and (max-width: 1024px) {
		padding: 30px 20%;
	}

	@media only screen and (max-width: 600px) {
		padding: 30px 10%;
	}
`

const RoundedContainer = styled.div`
	border-radius: 25px;
	overflow: hidden;
`

const WOutlineBtn = styled.button`
	${tw`w-full md:w-[150px] px-4 h-10 border border-2 rounded-full text-white hover:bg-white hover:text-black ease-in-out duration-100`}
	fontFamily: 'Inter',
	fontWeight: '700',
	fontSize: '18px',
	lineHeight: '22px',
	textAlign: 'center',
	color: '#f2f2f2',
`

const OutlineBtn = styled.button`
	${tw`w-full md:w-[150px] px-4 h-10 border border-2 rounded-full text-black hover:bg-gray-700 hover:text-white ease-in-out duration-100`}
	fontFamily: 'Inter',
	fontWeight: '700',
	fontSize: '18px',
	lineHeight: '22px',
	textAlign: 'center',
	color: '#1E1E1E',
`

const LightBlueBtn = styled.button`
	${tw`w-full md:w-[150px] px-4 h-10 font-bold rounded-full bg-blue-400 hover:bg-blue-500 hover:text-white ease-in-out duration-100`}
	fontFamily: 'Inter',
	fontWeight: '700',
	fontSize: '18px',
	lineHeight: '22px',
	textAlign: 'center',
	color: '#F2F2F2',
`

const SecondaryBtn = styled.button`
	${tw`w-full md:w-[150px] px-4 h-10 font-bold rounded-full bg-[#fdb515] hover:bg-[#fdbd2e] ease-in-out duration-100`}
	fontFamily: 'Inter',
	fontWeight: '700',
	fontSize: '18px',
	lineHeight: '22px',
	textAlign: 'center',
	color: '#1E1E1E',
`

const HeroSection = styled.section`
	${tw`bg-cover bg-center bg-no-repeat relative max-h-screen`}
`

const features = [
	[
		{
			title: 'INDUCTIVE CHARGING',
			content: (
				<span css={[`text-wrap: stable`]}>
					The new generation of Liberty cordless pool cleaners is enhanced with
					an inductive, magnetic connection that allows for easy click-in-place
					and walk-away charging.
				</span>
			),
		},
		{
			title: 'EFFORTLESS RETRIEVAL',
			content: (
				<span css={[`text-wrap: stable`]}>
					Retrieving your Dolphin Liberty is quick and{' '}
					<span css={tw`whitespace-nowrap`}>hassle-free</span>. The{' '}
					<span css={tw`font-bold`}>Liberty 400</span> includes the{' '}
					<span css={tw`font-bold`}>ClickUp™</span> sound-activated clicker,
					which signals the cleaner to come to you for easy removal. It also
					includes <span css={tw`font-bold`}>Pick-Up Mode</span>, ensuring it
					stops at the waterline for effortless retrieval. Both the{' '}
					<span css={tw`font-bold`}>Liberty 400 and Liberty 200</span> come with
					a <span css={tw`font-bold`}>retrieval hook</span>, providing a simple
					way to lift the cleaner from the water without strain.
				</span>
			),
		},
		{
			title: 'SUPERIOR FILTRATION ',
			content: (
				<span css={[`text-wrap: stable`]}>
					The Liberty range of cordless pool cleaners delivers powerful,
					independent cleaning with fine and ultra-fine filtration. It
					effectively captures dirt, dust, and debris of all sizes, keeping your
					pool spotless without relying on the pool’s filtration system.
				</span>
			),
		},
		{
			title: 'COMPLETE POOL COVERAGE',
			content: (
				<span css={[`text-wrap: stable`]}>
					The <span css={tw`font-bold`}>Dolphin Liberty</span> ensures a{' '}
					<span css={tw`font-bold`}>thorough clean</span> on all pool surfaces,
					including the{' '}
					<span css={tw`font-bold`}>floor, walls, and waterline</span>. It even
					tackles{' '}
					<span css={tw`font-bold`}> shallow sun ledges as low as 30cm</span>,
					reaching hard-to-clean areas for a{' '}
					<span css={tw`font-bold`}>deep, efficient clean</span> every time.
				</span>
			),
		},
	],
	[
		{
			title: 'CLEVERCLEAN™ \nPRECISE NAVIGATION SYSTEM',
			content: (
				<span>
					<Link
						css={tw`text-blue-500`}
						to="/en-NZ/support/faq/cleverclean-tm-coverage-precise-navigation-system"
					>
						CleverClean™
					</Link>{' '}
					is an advanced navigation system that ensures the Dolphin Liberty
					robot covers every inch of your pool. Using smart scanning technology
					and intelligent algorithms, it efficiently maps out the most effective
					cleaning path, avoiding obstacles and ensuring thorough coverage for a
					spotless pool every time.
				</span>
			),
		},
		{
			title: 'CUSTOMISED CLEANING CYCLES',
			content: (
				<span css={[`text-wrap: stable`]}>
					The{' '}
					<Link css={tw`text-blue-500`} to="/product/dolphin-liberty-200">
						Liberty 200
					</Link>{' '}
					and 400 feature an Eco Mode that keeps your pool floor clean all week
					with shorter, more efficient cleaning cycles on a single charge. The
					Liberty 400 has an optional cleaning cycle selector that customises
					the cleaning mode between floor-only and complete coverage of all pool
					surfaces.
				</span>
			),
		},
		{
			title: 'MYDOLPHIN™ PLUS APP',
			content: (
				<span css={[`text-wrap: stable`]}>
					Enjoy effortless, convenient control of the{' '}
					<span css={tw`font-bold`}>Liberty 400</span> with the{' '}
					<Link css={tw`text-blue-500`} to="/support/faq/mydolphin-plus-app">
						MyDolphin™ Plus app
					</Link>
					. Easily activate your cleaner, select cleaning modes, enable the
					advanced Eco Mode, and receive end-of-cycle notifications—all through
					seamless Bluetooth® connectivity. Pool cleaning has never been easier!
				</span>
			),
		},
		{
			title: 'CLIMATE CARE CERTIFIED',
			content: (
				<span css={[`text-wrap: stable`]}>
					<Link css={tw`text-blue-500`} to="/">
						Dolphin robotic pool cleaners
					</Link>{' '}
					are{' '}
					<Link css={tw`text-blue-500`} to="/climate-care-certified/">
						Climate Care Certified
					</Link>
					, showcasing their compliance with stringent sustainability standards
					set by SPASA. Independently verified for water and energy efficiency,
					noise reduction, and eco-friendly design, they deliver a
					cost-effective and eco-friendly way to keep your pool sparkling.
				</span>
			),
		},
	],
]

const compareTableRowData = [
	{
		title: 'POOL LENGTH',
		items: ['Up to 10m', 'Up to 10m', 'Up to 15m'],
	},
	{
		title: 'CLEANING COVERAGE',
		items: [
			'Floor, walls, waterline & sun ledges',
			'Floor, walls, waterline & sun ledges',
			'Floor, walls, waterline, sun ledges & steps',
		],
	},
	{
		title: 'RUN TIME',
		items: ['Up to 2.5 hours', 'Up to 2.5 hours', 'Up to 4.5 hours'],
	},
	{
		title: 'ECO MODE',
		items: [
			'Floor only (basic)',
			'3 cycle options',
			'6 advanced cycle options',
		],
	},
	{
		title: 'FILTER',
		items: [
			'Fine 2.5L filter basket',
			'Ultra-fine 2.5L filter basket',
			'Multi-layer 4.5L filter basket',
		],
	},
	{
		title: 'APP CONNECTIVITY',
		items: [
			'✗',
			'MyDolphin™ Plus (Bluetooth)',
			'Maytronics One™ (Bluetooth/Wi-Fi)',
		],
	},
	{
		title: 'CLICK-UP™',
		items: ['✗', '✔', '✔ + Click-To-Stop'],
	},
	{
		title: 'WARRANTY',
		items: ['2 years', '3 years', '3 years'],
	},
]

const CompareTableRow = ({ title, items }) => {
	return (
		<tr css={[tw`text-xs divide-x divide-white`]}>
			<td css={[tw`bg-blue-500 text-white font-bold py-2`]}> {title} </td>
			{items.map((item, i) => {
				return (
					<td key={i} css={[tw`bg-gray-200 py-2`]}>
						{' '}
						{item}{' '}
					</td>
				)
			})}
		</tr>
	)
}

const LibertyCordless = props => {
	// The state for the overlay is no longer needed since it's being replaced.
	// const [registerOverlay, setRegisterOverlay] = useState(false)
	const {
		LibertyHeroBannerVideo,
		LibertyHeroBannerImagePlaceholder,
		LibertyModelImage01,
		WhyChooseLiberty01,
		WhyChooseLiberty02,
		Liberty600asset,
		Liberty400asset,
		Liberty200asset,
		LibertyLifestyle,
		Liberty200TableAsset,
		Liberty400TableAsset,
		Liberty600TableAsset,
		HighPerfLowCostAsset1,
		HighPerfLowCostAsset2,
		HighPerfLowCostAssetVideo01,
		SmartSimpleCordlessAsset1,
		SmartSimpleCordlessAsset2,
		WhyChooseLibertyVideo01,
		WhyChooseLibertyVideo02,
		DiscoverLibertyPromotionalVideo01,
		DolphinLibertyTagLine,
	} = props.data

	return (
		<div>
			<Layout>
				<Meta
					title="Liberty Line | Robotic Cordless Pool Cleaner"
					keywords={[
						'liberty-line',
						'pool',
						'equipment',
						'liberty-cordless',
						'liberty cordless',
					]}
					description="Dolphin Liberty Robots Designed By Maytronics With A UNIQUE Battery-Powered Cordless Robotic Pool Cleaner SPECIALISED For Irregularly Shaped Pools."
				/>

				{/* Tracking Script */}
				<script
					type="application/javascript"
					dangerouslySetInnerHTML={{
						__html: `(function(w,d,t,r,u){w[u]=w[u]||[];w[u].push({'projectId':'10000','properties':{'pixelId':'10206112','he': '<email_address>','hph': '<phone_number>'}});var s=d.createElement(t);s.src=r;s.async=true;s.onload=s.onreadystatechange=function(){var y,rs=this.readyState,c=w[u];if(rs&&rs!="complete"&&rs!="loaded"){return}try{y=YAHOO.ywa.I13N.fireBeacon;w[u]=[];w[u].push=function(p){y([p])};y(c)}catch(e){}};var scr=d.getElementsByTagName(t)[0],par=scr.parentNode;par.insertBefore(s,scr)})(window,document,"script","https://s.yimg.com/wi/ytc.js","dotq");`
					}}
				/>

				{/* Hero Video Section */}
				<div css={[tw`relative w-full`, { height: '45vh' }]}>
					<VideoPlayer
						videoSrc={LibertyHeroBannerVideo.nodes[0].file.url}
						autoPlay={false}
						centerButtons={true}
						playButtonSize={{ width: '180', height: '180' }}
						pauseButtonSize={{ width: '60', height: '60' }}
					/>

					{/* Dolphin Liberty Text Overlay - Top Left */}
					<div css={[tw`absolute top-6 w-full px-6`]}>
						<div css={[tw`w-full max-w-screen-lg mx-auto `]}>
							<img
								src={DolphinLibertyTagLine.nodes[0].svg.url}
								alt={
									DolphinLibertyTagLine.nodes[0].title || 'Liberty Line Logo'
								}
								css={[tw`w-3/4 h-auto md:w-1/2`]}
							/>
						</div>
					</div>

					{/* Buy Now Button */}
					<div
						css={[
							tw`absolute bottom-6 left-1/2`,
							{ transform: 'translateX(-50%)' },
						]}
					>
						<button
							css={[
								tw`px-10 py-2 bg-white text-black font-bold rounded-full hover:bg-gray-100 transition-colors duration-200 shadow-lg`,
							]}
							onClick={() => navigate('/en-ZA/dolphin/liberty-line')}
						>
							BUY NOW
						</button>
					</div>
				</div>

				{/* Why choose Dolphin Liberty section*/}
				<div
					css={[
						tw`text-center px-4 pt-8 pb-2 md:pt-12 md:pb-3 lg:pt-16 lg:pb-4 mx-auto max-w-screen-lg w-full`,
					]}
				>
					<h1 css={[tw`font-bold text-lg text-black md:text-xl lg:text-2xl`]}>
						<span css={[tw`font-bold`]}>Cordless pool Cleaners:</span>
						<br />
						<span css={[tw`font-bold ml-2 md:ml-4 lg:ml-6`]}>
							Ultimate Freedom for Effortless Cleaning
						</span>
					</h1>
					<p
						css={[
							tw`mt-3 mx-auto px-4 md:px-16 text-center text-sm md:text-base`,
						]}
					>
						{' '}
						Say goodbye to tangled cords and hello to effortless pool care with
						Maytronics&apos; cordless robotic pool cleaners. Engineered for
						convenience, these battery-powered cleaners combine powerful
						suction, smart navigation, and long-lasting battery life to deliver
						a spotless pool-without the hassle. Whether you need quick touch-ups
						or deep-cleaning performance, our cable-free technology makes pool
						maintenance easier than ever.
					</p>
					{/* Image section */}
					<div css={[tw`text-center mt-6`]}>
						<Img
							{...WhyChooseLiberty01.nodes[0]}
							loading="lazy"
							css={[
								tw`w-full mx-auto rounded-3xl`,
								`height: 300px;`,
								css`
									img {
										object-fit: cover !important;
										object-position: 50% 70% !important;
									}
								`,
							]}
						/>
					</div>

					<div css={[tw`text-center mt-6 max-w-3xl mx-auto`]}>
						<ul css={[tw`list-none space-y-2`]}>
							<li>
								• <span css={[tw`font-bold`]}>100% cordless freedom</span> – no
								tangled cables, complete pool cleaning coverage
							</li>
							<li>
								• <span css={[tw`font-bold`]}>Effortless</span> click-in-place
								and walk away charging
							</li>
							<li>
								• <span css={[tw`font-bold`]}>Smart Dolphin CleverClean™</span>{' '}
								navigation ensures optimum, efficient cleaning every cycle
							</li>
							<li>
								• <span css={[tw`font-bold`]}>ECO Mode</span> for extending
								cleaning cycles on a single charge
							</li>
							<li>
								• <span css={[tw`font-bold`]}>Eco-Friendly,</span> Climate Care
								Certified to be water and energy efficient, helping reduce
								energy bills by up to $350 per year
							</li>
						</ul>
					</div>
				</div>

				{/* Meet the Liberty Range section */}
				<div
					css={[
						tw`text-center px-4 pt-8 pb-2 md:pt-12 md:pb-3 lg:pt-16 lg:pb-4 mx-auto max-w-screen-lg w-full`,
					]}
				>
					<h1
						css={[tw`font-bold text-lg text-black md:text-xl lg:text-2xl mb-6`]}
					>
						<span css={[tw`font-bold mb-4`]}>Meet the Liberty™ Range</span>
					</h1>

					{/* Grid Container - 2 columns, 3 rows */}
					<div
						css={[
							tw`grid md:grid-cols-2 gap-x-4 gap-y-10 md:gap-x-6 md:gap-y-14 lg:gap-x-8 lg:gap-y-16 mt-8 max-w-6xl mx-auto`,
						]}
					>
						{/* Grid Item 1 - Liberty 600 Content */}
						<div
							css={[
								tw`bg-white rounded-lg items-start text-center md:text-left `,
							]}
						>
							<div css={[tw`md:hidden rounded-2xl overflow-hidden`]}>
								<Img
									{...Liberty600asset.nodes[0]}
									loading="lazy"
									css={[tw`w-full`, `aspect-ratio: 675/400;`]}
								/>
							</div>
							<h3
								css={[
									tw`text-xl md:text-2xl lg:text-3xl font-bold text-black mb-2 mt-10 md:mt-0`,
								]}
							>
								Liberty™ 600
							</h3>
							<p
								css={[
									tw`text-base md:text-lg lg:text-xl md:italic font-semibold  mb-3 text-blue-600`,
								]}
							>
								Best for full-featured performance
							</p>
							<p
								css={[
									tw`text-xs md:text-sm text-gray-600 mb-4 leading-relaxed`,
								]}
							>
								Advanced, full-featured cordless power with premium control.
							</p>
							<ul
								css={[
									tw`inline-flex flex-col text-sm md:text-base lg:text-lg text-gray-700 text-left space-y-2 mx-auto md:mx-0`,
								]}
							>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									Up to 4.5-hour runtime
								</li>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									Jet-powered step cleaning
								</li>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									Maytronics One™ app control
								</li>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									Multi-layer 4.5L filter basket
								</li>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									Homing, Click-Up™ & Click-To-Stop retrieval
								</li>
							</ul>
							<button
								css={[
									tw`block bg-black hover:bg-gray-800 text-white text-xs md:text-sm font-medium py-2 px-8 rounded-lg transition-colors duration-200 mt-4 mx-auto md:mx-0 self-start mt-14`,
								]}
								onClick={() => window.open('https://mydolphin.co.za/product/dolphin-liberty-600/', '_blank')}
							>
								DISCOVER LIBERTY™ 600
							</button>
						</div>

						{/* Grid Item 2 - Liberty 600 Image */}
						<div css={[tw`hidden md:block`]}>
							<div css={[tw`rounded-2xl overflow-hidden`]}>
								<Img
									{...Liberty600asset.nodes[0]}
									loading="lazy"
									css={[tw`w-full`, `aspect-ratio: 675/400;`]}
								/>
							</div>
						</div>

						{/* Grid Item 3 */}
						<div css={[tw``]}>
							<div css={[tw`rounded-2xl overflow-hidden`]}>
								<Img
									{...Liberty400asset.nodes[0]}
									loading="lazy"
									css={[tw`w-full`, `aspect-ratio: 675/400;`]}
								/>
							</div>
						</div>

						{/* Grid Item 4 */}
						<div
							css={[
								tw`bg-white rounded-lg items-start text-center md:text-left  `,
							]}
						>
							<h3
								css={[
									tw`text-xl md:text-2xl lg:text-3xl font-bold text-black mb-2 md:mt-0`,
								]}
							>
								Liberty™ 400
							</h3>
							<p
								css={[
									tw`text-base md:text-lg lg:text-xl md:italic font-semibold  mb-3 text-blue-600`,
								]}
							>
								Best for smart convenience
							</p>
							<p
								css={[
									tw`text-xs md:text-sm text-gray-600 mb-4 leading-relaxed`,
								]}
							>
								Smart app control & Click-Up™ convenience.
							</p>
							<ul
								css={[
									tw`inline-flex flex-col text-sm md:text-base lg:text-lg text-gray-700 text-left space-y-2 mx-auto md:mx-0`,
								]}
							>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									MyDolphin Plus™ Bluetooth app control
								</li>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									Click-Up™ sonic retrieval
								</li>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									Multiple cleaning modes
								</li>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									Ultra-fine filtration
								</li>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									Accessory Organiser included
								</li>
							</ul>
							<button
								css={[
									tw`block bg-black hover:bg-gray-800 text-white text-xs md:text-sm font-medium py-2 px-8 rounded-lg transition-colors duration-200 mt-4 mx-auto md:mx-0 self-start mt-14`,
								]}
								onClick={() => window.open('https://mydolphin.co.za/product/dolphin-liberty-400/', '_blank')}
							>
								DISCOVER LIBERTY™ 400
							</button>
						</div>

						{/* Grid Item 5 - Liberty 200 Content */}
						<div
							css={[
								tw`bg-white rounded-lg items-start text-center md:text-left mb-4`,
							]}
						>
							<div css={[tw`md:hidden rounded-2xl overflow-hidden`]}>
								<Img
									{...Liberty200asset.nodes[0]}
									loading="lazy"
									css={[tw`w-full`, `aspect-ratio: 675/400;`]}
								/>
							</div>
							<h3
								css={[
									tw`text-xl md:text-2xl lg:text-3xl font-bold text-black mb-2 mt-10 md:mt-0`,
								]}
							>
								Liberty™ 200
							</h3>
							<p
								css={[
									tw`text-base md:text-lg lg:text-xl md:italic font-semibold  mb-3 text-blue-600`,
								]}
							>
								Best for simple everyday cleaning
							</p>
							<p
								css={[
									tw`text-xs md:text-sm text-gray-600 mb-4 leading-relaxed max-w-[300px] mx-auto md:max-w-none md:mx-0`,
								]}
							>
								{' '}
								Everyday cordless cleaning made simple.
							</p>
							<ul
								css={[
									tw`inline-flex flex-col text-sm md:text-base lg:text-lg text-gray-700 text-left space-y-2 mx-auto md:mx-0`,
								]}
							>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									Floor, walls, waterline & sun-ledge cleaning
								</li>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									ECO Mode - Floor-only maintenance
								</li>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									Inductive Charging
								</li>
								<li css={[tw``]}>
									<span css={[tw`text-blue-500 mr-2`]}>•</span>
									2.5-hour runtime
								</li>
							</ul>
							<button
								css={[
									tw`block bg-black hover:bg-gray-800 text-white text-xs md:text-sm font-medium py-2 px-8 rounded-lg transition-colors duration-200 mt-4 mx-auto md:mx-0 self-start mt-14`,
								]}
								onClick={() => window.open('https://mydolphin.co.za/product/dolphin-liberty-200/', '_blank')}
							>
								DISCOVER LIBERTY™ 200
							</button>
						</div>

						{/* Grid Item 6 - Liberty 200 Image */}
						<div css={[tw`hidden md:block`]}>
							<div css={[tw`rounded-2xl overflow-hidden`]}>
								<Img
									{...Liberty200asset.nodes[0]}
									loading="lazy"
									css={[tw`w-full`, `aspect-ratio: 675/400;`]}
								/>
							</div>
						</div>
					</div>
				</div>

				{/* Compare the Libery Line section */}
				<div
					css={[
						tw`text-center px-4 pt-8 pb-2 md:pt-12 md:pb-3 lg:pt-16 lg:pb-4 mx-auto max-w-screen-lg w-full`,
					]}
				>
					<h1 css={[tw`font-bold text-lg text-xl lg:text-2xl mb-2`]}>
						<span css={[tw`font-bold`]}>
							Compare the Dolphin Liberty™ Cordless Line
						</span>
					</h1>
					<h3 css={[tw`text-lg mb-4`]}>
						{' '}
						Find the Dolphin Liberty™ model that fits your pool.
					</h3>

					<div css={[tw`w-full overflow-x-auto`]}>
						<table
							css={[tw`w-full min-w-[700px] divide-y divide-x divide-white`]}
						>
							<tr css={[tw`divide-x divide-white`]}>
								<th> </th>
								<th css={[tw`bg-blue-500 text-white font-bold py-2`]}>
									DOLPHIN LIBERTY 200
								</th>
								<th css={[tw`bg-blue-500 text-white font-bold py-2`]}>
									DOLPHIN LIBERTY 400
								</th>
								<th css={[tw`bg-blue-500 text-white font-bold py-2`]}>
									DOLPHIN LIBERTY 600
								</th>
							</tr>

							<tr>
								<td> </td>
								<td css={[tw`py-4 px-2`]}>
									<Img
										{...Liberty200TableAsset.nodes[0]}
										loading="lazy"
										css={[tw`w-full`, `aspect-ratio: 675/400;`]}
									/>
								</td>
								<td css={[tw`py-4 px-4`]}>
									<Img
										{...Liberty400TableAsset.nodes[0]}
										loading="lazy"
										css={[tw`w-full`, `aspect-ratio: 675/400;`]}
									/>
								</td>
								<td css={[tw`py-4 px-4`]}>
									<Img
										{...Liberty600TableAsset.nodes[0]}
										loading="lazy"
										css={[tw`w-full`, `aspect-ratio: 675/400;`]}
									/>
								</td>
							</tr>

							{compareTableRowData.map((row, i) => {
								return <CompareTableRow key={i} {...row} />
							})}

							<tr css={[tw``]}>
								<td> </td>
								<td css={[tw`py-4`]}>
									<button
										css={[
											tw`text-sm px-10 py-2 bg-blue-500 text-white rounded-full font-bold hover:text-blue-500 hover:bg-black transition-colors duration-200 shadow-lg`,
										]}
										onClick={() => window.open('https://mydolphin.co.za/product/dolphin-liberty-200/', '_blank')}
									>
										BUY NOW
									</button>
								</td>

								<td>
									<button
										css={[
											tw`text-sm px-10 py-2 bg-blue-500 text-white rounded-full font-bold hover:text-blue-500 hover:bg-black transition-colors duration-200 shadow-lg`,
										]}
										onClick={() => window.open('https://mydolphin.co.za/product/dolphin-liberty-400/', '_blank')}
									>
										BUY NOW
									</button>
								</td>

								<td>
									<button
										css={[
											tw`text-sm px-10 py-2 bg-blue-500 text-white rounded-full font-bold hover:text-blue-500 hover:bg-black  transition-colors duration-200 shadow-lg`,
										]}
										onClick={() => window.open('https://mydolphin.co.za/product/dolphin-liberty-600/', '_blank')}
									>
										BUY NOW
									</button>
								</td>
							</tr>
						</table>
					</div>

					<section
						css={[
							tw`py-6 grid lg:grid-cols-12 gap-4 lg:py-4 mx-auto px-4 max-w-screen-lg mt-4 w-full`,
						]}
					>
						<RoundedContainer
							css={tw`
							object-cover w-full mx-auto items-center justify-center
							lg:col-span-9
							xl:col-span-9 h-[20rem] md:h-[28rem] shadow-xl`}
						>
							<VideoPlayer
								videoSrc={WhyChooseLibertyVideo01.nodes[0].file.url}
								autoPlay={false}
							/>
						</RoundedContainer>

						<RoundedContainer
							css={tw`
							object-cover w-full mx-auto items-center justify-center
							lg:col-span-3
							xl:col-span-3 h-[20rem] md:h-[28rem] shadow-xl`}
						>
							<Img
								{...LibertyLifestyle.nodes[0]}
								loading="lazy"
								css={[
									tw`w-full h-full mx-auto rounded-3xl`,

									css`
										img {
											object-fit: cover !important;
											object-position: 40% 70% !important;
										}
									`,
								]}
							/>
						</RoundedContainer>
					</section>
				</div>

				{/* High Performance, Low Effort, Low Costs*/}
				<div
					css={[
						tw`text-center px-4 pt-8 pb-2 md:pt-12 md:pb-3 lg:pt-16 lg:pb-4 mx-auto max-w-screen-lg w-full`,
					]}
				>
					<h1
						css={[tw`font-bold text-lg text-black md:text-xl lg:text-2xl mb-6`]}
					>
						<span css={[tw`font-bold mb-4`]}>
							High Performance, Low Effort, Low Costs
						</span>
						<br />
					</h1>
					<p css={[tw`mt-3 mx-auto text-center text-sm md:text-base`]}>
						{' '}
						Maytronics’ cordless cleaners are designed to keep your pool
						sparkling with minimal effort and lower energy costs. With ECO Mode,
						your cleaner optimises energy consumption, allowing you to maintain
						a crystal-clear pool for up to two weeks on a single charge. No
						cords, no hassle-just effortless, energy-efficient cleaning so you
						can spend more time enjoying your pool and less time maintaining it.
					</p>

					{/* Image section */}
					<div css={[tw`text-center mt-6 mx-auto w-full h-[400px]`]}>
						<Img
							{...HighPerfLowCostAsset1.nodes[0]}
							loading="lazy"
							css={[
								tw`h-full mx-auto rounded-3xl`,

								css`
									img {
										object-fit: cover !important;
									}
								`,
							]}
						/>
					</div>

					<section
						css={[
							tw`py-2 grid lg:grid-cols-12 gap-4 lg:py-4 mx-auto mt-4 w-full`,
						]}
					>
						<RoundedContainer
							css={tw`
							object-cover w-full mx-auto items-center justify-center
							lg:col-span-7
							xl:col-span-7 h-[18rem] md:h-[24rem] shadow-xl`}
						>
							<Img
								{...HighPerfLowCostAsset2.nodes[0]}
								loading="lazy"
								css={[
									tw`w-full h-full mx-auto rounded-3xl`,

									css`
										img {
											object-fit: cover !important;
											object-position: 40% 70% !important;
										}
									`,
								]}
							/>
						</RoundedContainer>

						<RoundedContainer
							css={tw`
							object-cover w-full mx-auto items-center justify-center
							lg:col-span-5
							xl:col-span-5 h-[18rem] md:h-[24rem] shadow-xl`}
						>
							<VideoPlayer
								videoSrc={HighPerfLowCostAssetVideo01.nodes[0].file.url}
								autoPlay={false}
							/>
						</RoundedContainer>
					</section>
				</div>

				{/* Smart, Simple, Cordless section*/}
				<div
					css={[
						tw`text-center px-4 pt-4 pb-2 md:pt-12  lg:pt-16 mx-auto max-w-screen-lg w-full`,
					]}
				>
					<h1
						css={[
							tw`font-bold text-lg text-black md:text-xl lg:text-2xl xl:text-3xl mb-4 `,
						]}
					>
						<span css={[tw`font-bold`]}>Smart, Simple, Cordless</span>
					</h1>
					<p css={[tw`mt-3 mx-auto text-center text-sm md:text-base`]}>
						Maytronics robots are designed to be user-friendly, with an
						intuitive drop-in and go setup free from bulky, heavy equipment or
						cables. Retrieval is quick and simple with features like the
						innovative Click-Up™ sonic retrieval and Robot Homing. Designed for
						total freedom and convenience, they navigate every part of your
						pool-from the floor and walls to the waterline and even stairs-with
						ease, ensuring complete coverage without complex setup or
						restrictions.
					</p>

					{/* Images Container */}
					<div
						css={[tw`relative mt-4 md:mt-12 lg:mt-8 max-w-screen-lg mx-auto`]}
					>
						{/* 1st Image section */}
						<div css={[tw`mx-auto  w-full`]}>
							<RoundedContainer>
								<Img
									{...SmartSimpleCordlessAsset1.nodes[0]}
									loading="lazy"
									css={[
										tw`w-full mx-auto`,
										`;
									`,
										css`
											img {
												object-fit: cover !important;
												object-position: center !important;
											}
										`,
									]}
								/>
							</RoundedContainer>

							<Img
								{...SmartSimpleCordlessAsset2.nodes[0]}
								loading="lazy"
								css={[
									tw`w-[105%] ml-[-5%] mt-[-5%]`,
									`aspect-ratio: 2/1;
									 `,
									css`
										img {
											object-fit: cover !important;
											object-position: center !important;
										}
									`,
								]}
							/>
						</div>

						{/* 2nd image section */}
					</div>
				</div>

				{/* Ready to Experience Cordless Cleaning? */}
				<div
					css={[
						tw`text-center px-4 my-8 md:pt-4 md:pb-3 lg:pt-8 lg:pb-4 mx-auto max-w-screen-lg w-full`,
					]}
				>
					<h1
						css={[
							tw`font-bold text-lg text-black md:text-xl lg:text-2xl xl:text-3xl mb-4 `,
						]}
					>
						<span css={[tw`font-bold`]}>
							Ready To Experience Cordless Cleaning?
						</span>
					</h1>
					<h3 css={[tw`mb-4 text-base`]}>
						Explore the full <span css={[tw`font-bold`]}>Dolphin Liberty</span>{' '}
						range and take control of your pool cleaning - the smart, cordless
						way.
					</h3>

					<div
						css={[
							tw`pt-2 flex flex-col sm:flex-row gap-4 justify-center items-center`,
						]}
					>
						<button
							css={[
								tw`text-sm px-10 py-2 bg-black text-white rounded-full font-bold hover:bg-gray-100 hover:text-blue-500 transition-colors duration-200 shadow-lg`,
							]}
							onClick={() => navigate('/en-ZA/dolphin/liberty-line')}
						>
							BUY NOW
						</button>

						<button
							css={[
								tw`text-sm px-10 py-2 bg-black text-white rounded-full font-bold hover:bg-gray-100 hover:text-blue-500 transition-colors duration-200 shadow-lg`,
							]}
							onClick={() => navigate('/en-ZA/support/find-a-dealer/')}
						>
							FIND A DEALER
						</button>
					</div>
				</div>

				{/* <GreySection>
					<article>
						<p css={tw`text-white`}>
							The <span css={tw`font-bold text-blue-500`}>NEW</span> generation
							of cordless pool cleaners offers exceptional performance and peace
							of mind. <span css={tw`whitespace-nowrap`}>Loaded with</span>{' '}
							innovative features, the new{' '}
							<span css={tw`font-bold text-blue-500`}>Dolphin Liberty™</span>
							does an even better job of cleaning any pool type without the
							potential hassle of tangled cords. Discover why the{' '}
							<span css={tw`font-bold text-blue-500`}>
								Dolphin Liberty™
							</span>{' '}
							cordless robotic pool cleaners from{' '}
							<span css={tw`font-bold text-blue-500`}>Maytronics</span> are an
							effortless way to keep your swimming pool clean and sparkling.
						</p>
						<p css={tw`font-bold text-blue-500 mt-4`}>
							No more exhausting pool maintenance!
						</p>
					</article>

					<div css={tw`w-full grid gap-4 my-8 md:w-max md:grid-cols-2`}>
						<WOutlineBtn
							onClick={() => navigate(`/product/dolphin-liberty-200`)}
						>
							LIBERTY 200
						</WOutlineBtn>

						<LightBlueBtn
							onClick={() => navigate(`/product/dolphin-liberty-400`)}
						>
							LIBERTY 400
						</LightBlueBtn>

						<WOutlineBtn onClick={() => navigate(`/book-a-demo`)}>
							BOOK A DEMO
						</WOutlineBtn>

						<SecondaryBtn onClick={() => navigate(`/dolphin/liberty-line`)}>
							BUY NOW
						</SecondaryBtn>
					</div>
				</GreySection> */}

				{/* <WhiteSection css={tw`py-8 space-y-4`}>
					<div
						css={tw`w-full text-lg leading-none text-center text-blue-500 md:text-xl lg:text-2xl`}
					>
						<h3 css={tw`font-bold`}>
							Discover the new generation of <br />
							<Link to="/product/dolphin-liberty-400">
								Dolphin Liberty™ cordless robotic pool cleaners
							</Link>
						</h3>
					</div>

					<div css={tw`hidden  lg:grid lg:grid-cols-2`}>
						{features.map((feature, index) => (
							<AccordionIndex key={`feature-${index}`} items={feature} />
						))}
					</div>

					<div css={tw`grid gap-4 lg:hidden`}>
						<AccordionIndex
							items={[...features[0], ...features[1]]}
							rounded={true}
						/>
					</div>
				</WhiteSection> */}

				{/* Video Section - Video 1 */}
				{/* <section
					css={[tw`py-8 space-y-4 md:py-8 md:px-[10%] lg:py-8 lg:px-[10%]`, ]}
				>
					<video
						muted={true}
						autoPlay={true}
						loop={true}
						loading="lazy"
						css={[tw`w-full md:rounded-3xl`, `aspect-ratio: 16/9`]}
					>
						<source
							src={DiscoverLibertyPromotionalVideo01.nodes[0].file.url}
							type="video/mp4"
						></source>
					</video>
				</section> */}

				{/* Part One */}
				{/* <WhiteSection css={tw`py-8 space-y-4`}>
					<div
						css={tw`text-lg leading-none text-center mx-auto text-blue-500 md:text-xl lg:text-2xl`}
					>
						<h3 css={tw`font-bold`}>
							Why choose a cordless robotic pool cleaner?
						</h3>
					</div>

					<article className="feature-text-container" css={tw`space-y-4`}>
						<p css={tw`text-sm font-normal leading-5 font-sans md:text-base `}>
							Nobody builds a swimming pool to spend all their free time
							cleaning it. Our robotic swimming pool cleaners are designed to do
							all the hard work for you – removing dirt and other debris,
							scrubbing the floor and walls, and keeping your pool sparkling
							clean.
						</p>
						<p css={tw`text-sm font-normal leading-5 font-sans md:text-base`}>
							However, when it comes to irregular or unusually shaped pools,
							even the best robotic pool cleaners have been unable to operate
							because their low-voltage power cable can become tangled on
							islands, pillars, or bridges that are part of the pool’s design.
						</p>

						<p css={tw`text-sm font-normal leading-5 font-sans md:text-base`}>
							The new Dolphin Liberty cordless robotic pool cleaner will
							transform how you care for your swimming pool by providing an
							effortless and effective cleaning solution. You’ll get the same
							unmatched Dolphin performance without the potential hassle of
							tangled cords.
						</p>
						<p css={tw`text-sm font-normal leading-5 font-sans md:text-base`}>
							This unique battery-powered robotic pool cleaner was designed with
							advanced Lithium-ion battery technology, so it doesn’t need to be
							connected to an external power supply. It offers maximum suction
							power, and the Eco Mode allows extended pool cleaning on a single
							charge.
						</p>
					</article>
				</WhiteSection>

				<section
					css={[
						tw`py-4 grid lg:grid-cols-12 gap-4 md:px-[10%] lg:py-6 lg:px-[10%]`,
					]}
				>
					<RoundedContainer
						css={tw`
							object-cover h-full w-4/5 mx-auto items-center justify-center
							lg:col-span-7 lg:w-full
							xl:col-span-8 xl:h-[36rem]`}
					>
						<Img
							{...WhyChooseLiberty01.nodes[0]}
							loading="lazy"
							css={[
								tw`w-full h-full mx-auto object-fill rounded-3xl`,
								`aspect-ratio: 1/1;`,
							]}
						/>
					</RoundedContainer>

					<RoundedContainer
						css={tw`
							object-cover h-full w-4/5 mx-auto items-center justify-center
							lg:col-span-5 lg:w-full
							xl:col-span-4 xl:h-[36rem]`}
					>
						<video
							muted={true}
							autoPlay={true}
							loop={true}
							loading="lazy"
							css={[
								tw`w-full h-full object-cover rounded-3xl`,
								`aspect-ratio: 1/1;`,
							]}
						>
							<source
								src={WhyChooseLibertyVideo01.nodes[0].file.url}
								type="video/mp4"
							></source>
						</video>
					</RoundedContainer>
				</section>

				<section
					css={[
						tw`space-y-2 py-0`,
						`font-family: Inter;
						font-style: normal;
						align-items: center;
						justify-content: center;

						display: flex;
						flex-direction: column;
						padding: 0px 15%;

						@media only screen and (max-width: 1024px) {
							padding: 0px 15%;
						}

						@media only screen and (max-width: 600px) {
							padding: 0px 10%;
						}`,
					]}
				>
					<div
						className="feature-cta-container"
						css={tw`w-full grid gap-4 my-2 md:w-max md:grid-cols-2`}
					>
						<OutlineBtn onClick={() => navigate(`/book-a-demo`)}>
							BOOK A DEMO
						</OutlineBtn>

						<SecondaryBtn onClick={() => navigate(`/dolphin/liberty-line`)}>
							BUY NOW
						</SecondaryBtn>
					</div>
				</section> */}

				{/* Part Two */}
				{/* <WhiteSection css={tw`py-8 space-y-4 mt-4 md:mt-8`}>
					<article className="feature-text-container" css={tw`space-y-4`}>
						<p>
							The Dolphin Liberty cordless robot pool cleaner is an innovative
							statement. Its unmatched cleaning technology, filtration system,
							durable active scrubbing brushes, and customised cleaning options
							deliver a thorough cleaning job of all the pool’s surfaces.
						</p>
						<p>
							This new generation of cordless robotic pool cleaners boasts
							revolutionary technologies for increased efficiency and ease of
							use. When the cleaning cycle is finished, you can easily pick it
							up from the pool edge using the innovative{' '}
							<span css={tw`whitespace-nowrap`}>‘Click Up’ device</span> and use
							the Magnetic Connect for Inductive Charging.
						</p>

						<p>
							While the Dolphin Liberty is designed to work individually in
							pools up to 10 metres, its cordless design allows multiple robots
							to provide exceptional cleaning coverage in larger pools.
						</p>
						<p>
							You no longer have to manually scrub the pool walls or catch
							debris to enjoy crystal-clean pool water. With its innovative
							design and powerful cleaning performance, the new Dolphin Liberty
							cordless pool cleaner robot gives you your time back.
						</p>
						<p>
							Whether you have an above-ground or in-ground pool, regardless of
							the pool shape and surface, the new Dolphin Liberty is the best
							cordless robotic pool cleaner in Australia!
						</p>
					</article>
				</WhiteSection>

				<section
					css={[
						tw`py-4 grid lg:grid-cols-12 gap-4 md:px-[10%] lg:py-6 lg:px-[10%]`,
					]}
				>
					<RoundedContainer
						css={tw`
							object-cover h-full w-4/5 mx-auto items-center justify-center
							lg:col-span-5 lg:w-full
							xl:col-span-4 xl:h-[36rem]`}
					>
						<video
							muted={true}
							autoPlay={true}
							loop={true}
							loading="lazy"
							css={[
								tw`w-full h-full object-cover rounded-3xl`,
								`aspect-ratio: 1/1;`,
							]}
						>
							<source
								src={WhyChooseLibertyVideo02.nodes[0].file.url}
								type="video/mp4"
							></source>
						</video>
					</RoundedContainer>

					<RoundedContainer
						css={tw`
							object-cover h-full w-4/5 mx-auto items-center justify-center
							lg:col-span-7 lg:w-full
							xl:col-span-8 xl:h-[36rem]`}
					>
						<Img
							{...WhyChooseLiberty02.nodes[0]}
							loading="lazy"
							css={[
								tw`w-full h-full mx-auto object-fill rounded-3xl`,
								`aspect-ratio: 1/1;`,
							]}
						/>
					</RoundedContainer>
				</section>

				<section
					css={[
						tw`space-y-4 py-0 mb-8`,
						`font-family: Inter;
						font-style: normal;
						align-items: center;
						justify-content: center;

						display: flex;
						flex-direction: column;
						padding: 0px 15%;

						@media only screen and (max-width: 1024px) {
							padding: 0px 15%;
						}

						@media only screen and (max-width: 600px) {
							padding: 0px 10%;
						}`,
					]}
				>
					<div
						className="feature-cta-container"
						css={tw`w-full grid gap-4 my-2 md:w-max md:grid-cols-2 lg:grid-cols-4`}
					>
						<OutlineBtn onClick={() => navigate(`/book-a-demo`)}>
							BOOK A DEMO
						</OutlineBtn>
						<SecondaryBtn onClick={() => navigate(`/dolphin/liberty-line`)}>
							BUY NOW
						</SecondaryBtn>
						<OutlineBtn
							onClick={() => navigate(`/product/dolphin-liberty-200`)}
						>
							LIBERTY 200
						</OutlineBtn>
						<OutlineBtn
							onClick={() => navigate(`/product/dolphin-liberty-400`)}
						>
							LIBERTY 400
						</OutlineBtn>
					</div>
				</section> */}
			</Layout>
		</div>
	)
}

/**
 * Page content query
 * @private
 * @static
 * @returns {() => void}
 */
export const LibertyCordlessZaPageQuery = graphql`
	query LibertyCordlessZaPageQuery {
		LibertyHeroBannerVideo: allContentfulAsset(
			filter: {
				contentful_id: { eq: "4KrwJerbnlT7L1nmPmj9OP" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				file {
					url
				}
			}
		}

		LibertyHeroBannerImagePlaceholder: allContentfulAsset(
			filter: {
				contentful_id: { eq: "4TqzlckDyN73yDKFSZXQIp" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		LibertyModelImage01: allContentfulAsset(
			filter: {
				contentful_id: { eq: "5gkD7icDY4lYSOEK14ZUNc" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		Liberty600asset: allContentfulAsset(
			filter: {
				contentful_id: { eq: "3U0bYLP1xi93q0zpMkqb4W" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		Liberty400asset: allContentfulAsset(
			filter: {
				contentful_id: { eq: "5nVUU0iQMD53KQPiICfG0j" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		Liberty200asset: allContentfulAsset(
			filter: {
				contentful_id: { eq: "7e0veYWCAbc5H9H8L96qfk" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}

		WhyChooseLiberty01: allContentfulAsset(
			filter: {
				contentful_id: { eq: "5gkD7icDY4lYSOEK14ZUNc" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 1080, maxHeight: 878, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		WhyChooseLiberty02: allContentfulAsset(
			filter: {
				contentful_id: { eq: "5ncjeeTD4woU25NTydekNl" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 1080, maxHeight: 878, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		Liberty200TableAsset: allContentfulAsset(
			filter: {
				contentful_id: { eq: "4E17rg82ScefN4PbtkIH1h" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 1080, maxHeight: 878, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		Liberty400TableAsset: allContentfulAsset(
			filter: {
				contentful_id: { eq: "5LzDYUHR2NrIVr8g2NU9Cj" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 1080, maxHeight: 878, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		Liberty600TableAsset: allContentfulAsset(
			filter: {
				contentful_id: { eq: "7iN1oPdkcOBAMAdQd5ZxqR" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 1080, maxHeight: 878, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		LibertyLifestyle: allContentfulAsset(
			filter: {
				contentful_id: { eq: "73cSzYjQppWypg87jbeTY4" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 1080, maxHeight: 878, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}

		HighPerfLowCostAsset1: allContentfulAsset(
			filter: {
				contentful_id: { eq: "1jdec6UpJtATHZ9tMPFryL" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 1080, maxHeight: 878, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}

		HighPerfLowCostAsset2: allContentfulAsset(
			filter: {
				contentful_id: { eq: "7tEl2lcwL45rXOVMmLVDzR" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 1080, maxHeight: 878, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}

		HighPerfLowCostAssetVideo01: allContentfulAsset(
			filter: {
				contentful_id: { eq: "An33OTwLZwnbOIHdEA0e1" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				file {
					url
				}
			}
		}
		SmartSimpleCordlessAsset1: allContentfulAsset(
			filter: {
				contentful_id: { eq: "4sSyWGT8rNWOeyr0RMlNfF" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 1080, maxHeight: 878, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		SmartSimpleCordlessAsset2: allContentfulAsset(
			filter: {
				contentful_id: { eq: "7wmRqXUTYQXHXRiIIbsEjt" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 1080, maxHeight: 878, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}

		WhyChooseLibertyVideo01: allContentfulAsset(
			filter: {
				contentful_id: { eq: "1Oo4USQaWafoL2eqXJShZi" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				file {
					url
				}
			}
		}

		WhyChooseLibertyVideo02: allContentfulAsset(
			filter: {
				contentful_id: { eq: "1nDfkhabtjzBGAK2J2kcnv" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				file {
					url
				}
			}
		}

		DiscoverLibertyPromotionalVideo01: allContentfulAsset(
			filter: {
				contentful_id: { eq: "70Pvib7fMyagUhgaCLVPJG" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				file {
					url
				}
			}
		}

		DolphinLibertyTagLine: allContentfulAsset(
			filter: {
				contentful_id: { eq: "5gizc1q67w1PxxfoZs4CDU" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				svg: file {
					url
					contentType
				}
			}
		}
	}
`

LibertyCordless.propTypes = {
	props: PropTypes.shape({
		data: PropTypes.shape({
			LibertyHeroBannerVideo: PropTypes.object,
			LibertyHeroBannerImagePlaceholder: PropTypes.object,
			LibertyModelImage01: PropTypes.object,
			WhyChooseLiberty01: PropTypes.object,
			WhyChooseLiberty02: PropTypes.object,
			Liberty600asset: PropTypes.object,
			Liberty400asset: PropTypes.object,
			Liberty200asset: PropTypes.object,
			LibertyLifestyle: PropTypes.object,
			Liberty200TableAsset: PropTypes.object,
			Liberty400TableAsset: PropTypes.object,
			Liberty600TableAsset: PropTypes.object,
			HighPerfLowCostAsset1: PropTypes.object,
			HighPerfLowCostAsset2: PropTypes.object,
			HighPerfLowCostAssetVideo01: PropTypes.object,
			SmartSimpleCordlessAsset1: PropTypes.object,
			SmartSimpleCordlessAsset2: PropTypes.object,
			WhyChooseLibertyVideo01: PropTypes.object,
			WhyChooseLibertyVideo02: PropTypes.object,
			DiscoverLibertyPromotionalVideo01: PropTypes.object,
			DolphinLibertyTagLine: PropTypes.object,
		}),
	}),
}
export default LibertyCordless
